import { useEffect, useRef, useState, type ReactNode } from "react";
import {
  assignOutputResource,
  createOutputProfile,
  globalOutputSettings,
  outputRoleLabels,
  outputSettingsKey,
  uid,
  type OutputProfile,
  type Project,
  type Resource,
} from "@proof/core";
import { useAuthoringServices } from "./services";
import { Button, Modal } from "./components";

export function OutputLibrary({
  project,
  output,
  onChange,
  notify,
  renderEditor,
  canAssign = true,
}: {
  project: Project;
  output: OutputProfile;
  onChange(project: Project): void;
  notify(message: string): void;
  canAssign?: boolean;
  renderEditor(
    value: OutputProfile,
    onChange: (value: OutputProfile) => void,
  ): ReactNode;
}) {
  const services = useAuthoringServices();
  const [resources, setResources] = useState<Resource[]>([]);
  const [draft, setDraft] = useState<OutputProfile>();
  const [editing, setEditing] = useState<Resource>();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const current = useRef({ project, output, onChange, alive: true });
  current.current = { project, output, onChange, alive: true };
  useEffect(() => {
    current.current.alive = true;
    return () => {
      current.current.alive = false;
    };
  }, []);
  useEffect(() => {
    let active = true;
    setResources([]);
    void services
      .listResources?.()
      .then((rows) => {
        if (active)
          setResources(rows.filter((row) => row.tenantId === project.tenantId));
      })
      .catch((error) => {
        if (active) notify(error.message);
      });
    return () => {
      active = false;
    };
  }, [project.tenantId, project.id, services.listResources]);
  const rows = resources.filter(
    (row) =>
      row.kind === "output" &&
      !row.projectId &&
      !row.archived &&
      !resources.some(
        (other) => other.id === row.id && other.version > row.version,
      ),
  );
  const legacy = resources.filter(
    (row) =>
      row.kind === "output" &&
      row.projectId === project.id &&
      !row.archived &&
      !resources.some(
        (other) => other.id === row.id && other.version > row.version,
      ) &&
      !rows.some(
        (shared) =>
          (shared.value as OutputProfile).role ===
            (row.value as OutputProfile).role &&
          outputSettingsKey(shared.value as OutputProfile) ===
            outputSettingsKey(row.value as OutputProfile),
      ),
  );
  const open = (value: OutputProfile, resource?: Resource) => {
    setEditing(resource);
    setDraft(
      globalOutputSettings({ ...value, name: resource?.name || value.name }),
    );
    setError("");
  };
  const assign = (row: Resource) => {
    try {
      onChange(assignOutputResource(project, output.id, row));
      notify(
        `${row.name} v${row.version} assigned to ${outputRoleLabels[output.role!] || output.name}. Save the project draft to retain it.`,
      );
    } catch (error) {
      notify((error as Error).message);
    }
  };
  const save = async (assignAfter: boolean) => {
    if (!draft || !services.saveResource || busy) return;
    const owner = project.id,
      tenant = project.tenantId;
    setBusy(true);
    setError("");
    try {
      const name = draft.name.trim();
      if (!name) throw new Error("Enter a profile name");
      const row = await services.saveResource({
        id: editing?.id || uid("resource"),
        tenantId: tenant,
        kind: "output",
        name,
        version: editing?.version || 0,
        value: globalOutputSettings({ ...draft, name }),
      });
      if (
        !current.current.alive ||
        current.current.project.id !== owner ||
        current.current.project.tenantId !== tenant
      )
        return;
      setResources((previous) => [...previous, row]);
      setDraft(undefined);
      setEditing(undefined);
      if (assignAfter)
        current.current.onChange(
          assignOutputResource(
            current.current.project,
            current.current.output.id,
            row,
          ),
        );
      notify(
        `Global output profile ${name} v${row.version} saved${assignAfter ? " and assigned to this project" : ""}. Existing project assignments keep their saved versions.`,
      );
    } catch (error) {
      if (current.current.alive) setError((error as Error).message);
    } finally {
      if (current.current.alive) setBusy(false);
    }
  };
  if (!services.listResources && !services.saveResource) return null;
  return (
    <section
      className="panel padded"
      aria-label="Global output profile library"
    >
      <h2>Global output profiles</h2>
      <p>
        Create reusable profiles once, then assign a version to each project's
        output stage. Editing a global profile does not change existing
        assignments.
      </p>
      {services.saveResource && (
        <div className="button-row">
          <Button
            onClick={() => open(createOutputProfile(output.role || "high-res"))}
          >
            Create global output profile
          </Button>
          {canAssign && (
            <Button onClick={() => open(output)}>
              Save selected stage as global profile
            </Button>
          )}
        </div>
      )}
      {!rows.length && (
        <p className="muted">
          No global output profiles saved yet. Existing project output settings
          are preserved below.
        </p>
      )}
      <div className="catalog-entries">
        {rows.map((row) => {
          const definition = row.value as OutputProfile;
          const compatible =
            !definition.role || !output.role || definition.role === output.role;
          const assigned =
            output.outputPreset?.id === row.id &&
            output.outputPreset.version === row.version;
          return (
            <article
              className="saved-proof-row"
              key={`${row.id}:${row.version}`}
            >
              <div>
                <strong>
                  {row.name} · v{row.version}
                </strong>
                <small>
                  {definition.role
                    ? outputRoleLabels[definition.role] + " · "
                    : ""}
                  {definition.format.toUpperCase()} · {definition.dpi} DPI
                  {assigned ? " · Assigned to selected stage" : ""}
                </small>
              </div>
              <div className="button-row">
                <Button
                  disabled={!canAssign || !compatible || assigned}
                  title={
                    !compatible
                      ? `Select the ${outputRoleLabels[definition.role!]} project stage first`
                      : undefined
                  }
                  onClick={() => assign(row)}
                >
                  Assign to selected stage
                </Button>
                {services.saveResource && (
                  <Button onClick={() => open(definition, row)}>
                    Edit global profile
                  </Button>
                )}
              </div>
            </article>
          );
        })}
      </div>
      {!!legacy.length && services.saveResource && (
        <details>
          <summary>Previously project-owned output presets</summary>
          <p>
            Publish a global copy to reuse an older preset. Its original
            identity and existing project assignments are preserved.
          </p>
          {legacy.map((row) => (
            <div className="button-row" key={row.id}>
              <span>
                {row.name} · v{row.version}
              </span>
              <Button
                onClick={() =>
                  open({ ...(row.value as OutputProfile), name: row.name })
                }
              >
                Publish global copy
              </Button>
            </div>
          ))}
        </details>
      )}
      {draft && (
        <Modal
          title={
            editing
              ? "Edit global output profile"
              : "Create global output profile"
          }
          wide
          onClose={() => {
            if (!busy) setDraft(undefined);
          }}
        >
          <p>
            {editing
              ? `Save a new version of ${editing.name}. Assigned projects keep their current version.`
              : "This profile will be available to every project in this workspace."}
          </p>
          <fieldset
            disabled={busy}
            style={{ border: 0, padding: 0, minWidth: 0 }}
          >
            {renderEditor(draft, setDraft)}
            {error && <p role="alert">{error}</p>}
            <div className="button-row">
              <Button disabled={busy} onClick={() => void save(false)}>
                Save global profile
              </Button>
              {canAssign && (
                <Button
                  disabled={
                    busy ||
                    (!!draft.role &&
                      !!output.role &&
                      draft.role !== output.role)
                  }
                  onClick={() => void save(true)}
                >
                  Save and assign to project
                </Button>
              )}
              <Button disabled={busy} onClick={() => setDraft(undefined)}>
                Cancel
              </Button>
            </div>
          </fieldset>
        </Modal>
      )}
    </section>
  );
}
